// Cópia dos dados da empresa (LGPD, E9): uma planilha do Excel (.xlsx) para
// ler, com uma aba por assunto (nomes no lugar dos códigos internos, datas e
// valores no formato brasileiro, sem o que está na lixeira), e um arquivo
// .json com tudo, inclusive a lixeira e os códigos, para levar a outro
// sistema. Feita no aparelho, com o que está nele (funciona sem sinal). As
// fotos vão num .zip à parte, por ano (podem ser muitas).
import { strToU8 } from 'fflate';
import { celular as formatarCelular, documento as formatarDocumento } from './formato.js';
import { planilhaXlsx } from './xlsx.js';
import { TIPOS_DE_PROBLEMA } from './sos.js';

export const NOME_DA_PLANILHA = 'hsa-frota-dados.xlsx';

const TIPO_VEICULO = { trator: 'Caminhão', implemento: 'Carreta' };
const MOTIVO_INICIO = { inclusao: 'Placa ativada', troca: 'Troca de placa', implantacao: 'Início do uso' };
const MOTIVO_FIM = { retirada: 'Placa retirada', troca: 'Troca de placa' };
const FUNCAO = { motorista: 'Motorista', agenciador: 'Agenciador', outro: 'Outro' };
const COMPONENTE = {
  comissao_frete: 'Comissão sobre o frete', valor_por_carga: 'Valor por carga', valor_por_viagem: 'Valor por viagem',
  diaria: 'Diária', repasse_estadia: 'Repasse da estadia', fixo_mensal: 'Fixo mensal', agenciamento: 'Agenciamento',
};
const BASE = { bruto: 'Frete bruto', liquido: 'Frete líquido' };
const FIXO = { acerto: 'No acerto', folha: 'Pela folha' };
const GRUPO = { viagem: 'Viagem', manutencao: 'Manutenção e pneus', fixo: 'Fixos', imposto: 'Impostos e taxas', outros: 'Outros' };
const TIPO_CONTA = { mensal: 'Mensal', parcelado: 'Parcelada' };
const STATUS_VIAGEM = { aberta: 'Em andamento', encerrada: 'Encerrada', acertada: 'Acertada', cancelada: 'Cancelada' };
const PEDAGIO = { sem: 'Sem pedágio', contratante_tag: 'Tag do contratante', embutido: 'Embutido no frete' };
const COMPROVANTE = { nao_precisa: 'Não precisa', a_postar: 'A postar', postado: 'Postado', entregue: 'Entregue' };
const TIPO_LANCAMENTO = { vale: 'Vale', despesa: 'Despesa', credito: 'Crédito', desconto: 'Desconto', pagamento: 'Pagamento', devolucao: 'Devolução' };
const SUBTIPO = {
  comissao: 'Comissão', valor_por_carga: 'Valor por carga', valor_por_viagem: 'Valor por viagem', diaria: 'Diária',
  repasse_estadia: 'Repasse da estadia', agenciamento: 'Agenciamento', fixo_mensal: 'Fixo mensal', bonus: 'Bônus',
  outro: 'Outro', avaria: 'Avaria', multa: 'Multa',
};
const PAGO_POR = { empresa: 'Empresa', colaborador: 'Colaborador' };
const FORMA = {
  dinheiro: 'Dinheiro', pix: 'Pix', cartao: 'Cartão', boleto: 'Boleto', tag: 'Tag', transferencia: 'Transferência', faturado: 'Faturado', outro: 'Outro',
};
const CONDICAO = { a_vista: 'À vista', a_prazo: 'A prazo' };
const ORIGEM = { app: 'App', importacao: 'Programas antigos', implantacao: 'Saldo de implantação', sistema: 'Sistema', whatsapp: 'WhatsApp' };
const STATUS_ACERTO = { rascunho: 'Rascunho', fechado: 'Fechado', quitado: 'Quitado' };
const QUITACAO = { paga: 'Paga pela empresa', devolvida: 'Devolvida pela pessoa', transportada: 'Levada para o próximo acerto', zerado: 'Zerado' };
const TIPO_ANEXO = { foto: 'Foto', audio: 'Áudio', pdf: 'PDF', outro: 'Outro' };
const SITUACAO = { teste: 'Teste grátis', ativa: 'Ativa', somente_leitura: 'Só para consulta', cancelada: 'Cancelada' };
const RELATORIO = { competencia: 'Competência', caixa: 'Caixa' };

const vivo = (x) => x && !x.deleted_at;
const rotulo = (mapa, valor) => (valor == null ? null : mapa[valor] ?? valor);
const texto = (a, b) => String(a ?? '').localeCompare(String(b ?? ''), 'pt-BR', { sensitivity: 'base' });
const crescente = (...campos) => (a, b) => {
  for (const c of campos) {
    if ((a[c] ?? '') < (b[c] ?? '')) return -1;
    if ((a[c] ?? '') > (b[c] ?? '')) return 1;
  }
  return 0;
};

// Um momento (data e hora) como data do aparelho, 'AAAA-MM-DD'.
function diaDoMomento(momento) {
  if (!momento) return null;
  const d = new Date(momento);
  if (Number.isNaN(d.getTime())) return null;
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
const dataBR = (iso) => (iso ? String(iso).slice(0, 10).split('-').reverse().join('/') : '');

// As abas da planilha: [{ nome, colunas: [{ titulo, tipo, largura }], linhas }].
export function abasDaCopia(empresa, tabelas) {
  const t = (nome) => (tabelas[nome] ?? []).filter(vivo);
  const todas = (nome) => tabelas[nome] ?? [];
  const porId = (nome) => new Map(todas(nome).map((x) => [x.id, x]));
  const veiculos = porId('veiculos');
  const pessoas = porId('colaboradores');
  const clientes = porId('clientes');
  const categorias = porId('categorias');
  const viagens = porId('viagens');
  const fretes = porId('fretes');
  const lancamentos = porId('lancamentos');
  const acertos = porId('acertos');
  const contas = porId('contas_fixas');

  const placa = (id) => veiculos.get(id)?.placa ?? null;
  const itensDeManutencao = new Map((tabelas.manutencao_itens ?? []).map((i) => [i.id, i]));
  const pessoa = (id) => {
    const p = pessoas.get(id);
    return p ? (p.apelido ? `${p.apelido} (${p.nome})` : p.nome) : null;
  };
  const cliente = (id) => clientes.get(id)?.nome ?? null;
  const categoria = (id) => categorias.get(id)?.nome ?? null;
  const viagem = (id) => {
    const v = viagens.get(id);
    return v ? [dataBR(v.data_saida), placa(v.veiculo_id)].filter(Boolean).join(' · ') : null;
  };
  const carga = (id) => {
    const f = fretes.get(id);
    return f ? [dataBR(f.data_carregamento), cliente(f.cliente_id)].filter(Boolean).join(' · ') : null;
  };
  const acerto = (id) => {
    const a = acertos.get(id);
    return a ? (a.numero ? `nº ${a.numero}/${a.ano}` : 'rascunho') : null;
  };
  const despesa = (id) => {
    const l = lancamentos.get(id);
    return l ? [dataBR(l.data), categoria(l.categoria_id), l.fornecedor].filter(Boolean).join(' · ') : null;
  };
  const lugar = (cidade, uf) => [cidade, uf].filter(Boolean).join('/') || null;

  const abas = [];
  const aba = (nome, colunas, linhas) => abas.push({
    nome,
    colunas: colunas.map(([titulo, tipo = 'texto', largura = 14]) => ({ titulo, tipo, largura })),
    linhas,
  });

  aba('Empresa', [['Campo', 'texto', 26], ['Valor', 'texto', 44]], [
    [empresa.tipo_pessoa === 'PF' ? 'CPF' : 'CNPJ', formatarDocumento(empresa.documento)],
    ['Nome', empresa.nome],
    ['Nome fantasia', empresa.nome_fantasia],
    ['Celular', formatarCelular(empresa.celular)],
    ['E-mail', empresa.email],
    ['Endereço', empresa.endereco],
    ['Cidade', lugar(empresa.cidade, empresa.uf)],
    ['RNTRC', empresa.rntrc],
    ['Situação da conta', rotulo(SITUACAO, empresa.situacao)],
    ['Teste grátis até', dataBR(empresa.teste_ate)],
    ['Relatório para o contador', rotulo(RELATORIO, empresa.relatorio_contabil)],
  ]);

  aba('Caminhões e carretas',
    [['Tipo', 'texto', 11], ['Placa', 'texto', 11], ['Apelido', 'texto', 16], ['Marca', 'texto', 14], ['Modelo', 'texto', 16], ['Ano', 'inteiro', 8],
      ['RENAVAM', 'texto', 14], ['Engatada no caminhão', 'texto', 14], ['Km inicial', 'inteiro', 12], ['Ativo', 'sim/não', 8], ['Observação', 'texto', 30]],
    t('veiculos').sort((a, b) => (a.tipo === b.tipo ? texto(a.placa, b.placa) : a.tipo === 'trator' ? -1 : 1)).map((v) => [
      rotulo(TIPO_VEICULO, v.tipo), v.placa, v.apelido, v.marca, v.modelo, v.ano, v.renavam, placa(v.acoplado_a), v.hodometro_inicial, v.ativo, v.observacao,
    ]));

  aba('Placas ativas',
    [['Placa', 'texto', 11], ['Caminhão', 'texto', 16], ['Início', 'data', 12], ['Fim', 'data', 12], ['Como começou', 'texto', 16], ['Como terminou', 'texto', 16]],
    t('licencas').sort(crescente('placa', 'inicio')).map((l) => [
      l.placa, veiculos.get(l.veiculo_id)?.apelido ?? null, l.inicio, l.fim, rotulo(MOTIVO_INICIO, l.motivo_inicio), rotulo(MOTIVO_FIM, l.motivo_fim),
    ]));

  aba('Colaboradores',
    [['Nome', 'texto', 30], ['Apelido', 'texto', 14], ['Função', 'texto', 12], ['CPF', 'texto', 16], ['Celular', 'texto', 17], ['Chave Pix', 'texto', 24],
      ['Ativo', 'sim/não', 8], ['Observação', 'texto', 30]],
    t('colaboradores').sort((a, b) => texto(a.nome, b.nome)).map((p) => [
      p.nome, p.apelido, rotulo(FUNCAO, p.funcao), p.cpf ? formatarDocumento(p.cpf) : null, p.celular ? formatarCelular(p.celular) : null, p.chave_pix, p.ativo, p.observacao,
    ]));

  aba('Remuneração',
    [['Pessoa', 'texto', 26], ['Tipo', 'texto', 22], ['Percentual (%)', 'numero', 12], ['Valor', 'dinheiro', 12], ['Base', 'texto', 13],
      ['Inclui a estadia', 'sim/não', 10], ['Fixo pago', 'texto', 11], ['Frete até', 'dinheiro', 12], ['Valor acima', 'dinheiro', 12],
      ['Só no caminhão', 'texto', 12], ['Vale de', 'data', 12], ['Vale até', 'data', 12], ['Observação', 'texto', 30]],
    t('regras_remuneracao').sort((a, b) => texto(pessoa(a.colaborador_id), pessoa(b.colaborador_id)) || crescente('vigente_de')(a, b)).map((r) => [
      pessoa(r.colaborador_id), rotulo(COMPONENTE, r.componente), r.percentual, r.valor, rotulo(BASE, r.base), r.inclui_estadia,
      rotulo(FIXO, r.fixo_pagamento), r.faixa_ate, r.valor_acima, placa(r.veiculo_id), r.vigente_de, r.vigente_ate, r.observacao,
    ]));

  aba('Clientes',
    [['Nome', 'texto', 32], ['CPF ou CNPJ', 'texto', 20], ['Cidade', 'texto', 18], ['UF', 'texto', 5], ['Prazo do saldo (dias)', 'inteiro', 12],
      ['Ativo', 'sim/não', 8], ['Observação', 'texto', 30]],
    t('clientes').sort((a, b) => texto(a.nome, b.nome)).map((c) => [
      c.nome, c.documento ? formatarDocumento(c.documento) : null, c.cidade, c.uf, c.prazo_saldo_dias, c.ativo, c.observacao,
    ]));

  aba('Categorias',
    [['Nome', 'texto', 28], ['Grupo', 'texto', 18], ['Entra no custo por km', 'sim/não', 12], ['Criada pela empresa', 'sim/não', 12], ['Ativa', 'sim/não', 8]],
    t('categorias').sort((a, b) => (a.ordem ?? 999) - (b.ordem ?? 999) || texto(a.nome, b.nome)).map((c) => [
      c.nome, rotulo(GRUPO, c.grupo), c.entra_custo_km !== false, Boolean(c.empresa_id), c.ativo !== false,
    ]));

  aba('Contas fixas',
    [['Descrição', 'texto', 28], ['Categoria', 'texto', 18], ['Caminhão', 'texto', 11], ['Valor', 'dinheiro', 12], ['Dia do vencimento', 'inteiro', 10],
      ['Tipo', 'texto', 10], ['Parcelas', 'inteiro', 9], ['Primeiro mês', 'data', 12], ['Encerrada em', 'data', 12], ['Ativa', 'sim/não', 8]],
    t('contas_fixas').sort((a, b) => texto(a.descricao, b.descricao)).map((c) => [
      c.descricao, categoria(c.categoria_id), placa(c.veiculo_id), c.valor, c.dia_vencimento, rotulo(TIPO_CONTA, c.tipo), c.total_parcelas,
      c.primeira_competencia, c.encerrada_em, c.ativo,
    ]));

  aba('Viagens',
    [['Saída', 'data', 12], ['Chegada', 'data', 12], ['Caminhão', 'texto', 11], ['Motorista', 'texto', 26], ['Km na saída', 'inteiro', 12],
      ['Km na chegada', 'inteiro', 12], ['Km rodados', 'inteiro', 11], ['Situação', 'texto', 13], ['Acerto', 'texto', 11],
      ['Dos programas antigos', 'sim/não', 11], ['Observação', 'texto', 30]],
    t('viagens').sort(crescente('data_saida', 'created_at')).map((v) => [
      v.data_saida, v.data_chegada, placa(v.veiculo_id), pessoa(v.motorista_id), v.km_saida, v.km_chegada,
      v.km_saida != null && v.km_chegada != null ? v.km_chegada - v.km_saida : null,
      rotulo(STATUS_VIAGEM, v.status), acerto(v.acerto_id), Boolean(v.importacao_id), v.observacao,
    ]));

  aba('Cargas',
    [['Carregamento', 'data', 12], ['Descarga', 'data', 12], ['Caminhão', 'texto', 11], ['Motorista', 'texto', 22], ['Cliente', 'texto', 28],
      ['CT-e', 'texto', 12], ['Origem', 'texto', 18], ['Destino', 'texto', 18], ['Mercadoria', 'texto', 16], ['Peso (kg)', 'numero', 11],
      ['Frete', 'dinheiro', 12], ['Pedágio', 'texto', 17], ['Pedágio embutido', 'dinheiro', 12], ['INSS', 'dinheiro', 10], ['SEST/SENAT', 'dinheiro', 11],
      ['IRRF', 'dinheiro', 10], ['Outros descontos', 'dinheiro', 11], ['Outros descontos (o quê)', 'texto', 20], ['Líquido', 'dinheiro', 12],
      ['Estadia', 'dinheiro', 11], ['Adiantamento', 'dinheiro', 12], ['Adiantamento recebido em', 'data', 13], ['Saldo', 'dinheiro', 12],
      ['Saldo recebido em', 'data', 13], ['Agenciado por', 'texto', 20], ['Agenciamento', 'dinheiro', 12], ['Agenciamento pago em', 'data', 13],
      ['Comprovante', 'texto', 12], ['Comprovante postado em', 'data', 13], ['Rastreio', 'texto', 16], ['Acerto', 'texto', 11],
      ['Dos programas antigos', 'sim/não', 11], ['Observação', 'texto', 30], ['Complemento de', 'texto', 24], ['Entregue (data e hora)', 'texto', 18]],
    t('fretes').sort(crescente('data_carregamento', 'created_at')).map((f) => {
      const v = viagens.get(f.viagem_id);
      const agenciado = f.agenciado_por === 'transportadora' ? 'Transportadora' : f.agenciado_por === 'colaborador' ? pessoa(f.agenciador_id) : null;
      return [
        f.data_carregamento, f.data_descarga, placa(v?.veiculo_id), pessoa(v?.motorista_id), cliente(f.cliente_id), f.cte_numero,
        lugar(f.origem_cidade, f.origem_uf), lugar(f.destino_cidade, f.destino_uf), f.mercadoria, f.peso_kg, f.valor_frete,
        rotulo(PEDAGIO, f.pedagio_modo), f.pedagio_valor || null, f.desc_inss || null, f.desc_sest_senat || null, f.desc_irrf || null,
        f.desc_outros || null, f.desc_outros_descricao, f.valor_liquido, f.estadia_valor || null, f.adto_valor || null, f.adto_recebido_em,
        f.saldo_valor, f.saldo_recebido_em, agenciado, f.agenciamento_valor || null, f.agenciamento_pago_em, rotulo(COMPROVANTE, f.comprovante_status),
        f.comprovante_postado_em, f.comprovante_rastreio, acerto(f.acerto_id), Boolean(f.importacao_id), f.observacao,
        carga(f.complemento_de), f.entregue_em ? new Date(f.entregue_em).toLocaleString('pt-BR') : null,
      ];
    }));

  aba('Entregas no caminho',
    [['Carga', 'texto', 28], ['Ordem', 'inteiro', 8], ['Cidade', 'texto', 22], ['Entregue em', 'data', 12], ['Observação', 'texto', 30]],
    t('entregas').sort(crescente('frete_id', 'ordem')).map((e) => [carga(e.frete_id), e.ordem, lugar(e.cidade, e.uf), e.data_entrega, e.observacao]));

  aba('Lançamentos',
    [['Data', 'data', 12], ['Tipo', 'texto', 11], ['Categoria ou motivo', 'texto', 20], ['Valor', 'dinheiro', 12], ['Pessoa', 'texto', 24],
      ['Caminhão', 'texto', 11], ['Viagem', 'texto', 20], ['Carga', 'texto', 24], ['Pago por', 'texto', 12], ['Forma', 'texto', 13],
      ['Condição', 'texto', 9], ['Fornecedor', 'texto', 22], ['Nota', 'texto', 12], ['Sem nota (motivo)', 'texto', 16], ['Litros', 'numero', 9], ['Km', 'inteiro', 11],
      ['Descrição', 'texto', 30], ['Manutenção feita', 'texto', 30], ['Conta fixa', 'texto', 20], ['Mês da conta fixa', 'data', 12], ['Acerto', 'texto', 11],
      ['Assinado em', 'data', 12], ['Origem', 'texto', 16]],
    t('lancamentos').sort(crescente('data', 'created_at')).map((l) => [
      l.data, rotulo(TIPO_LANCAMENTO, l.tipo), l.tipo === 'despesa' ? categoria(l.categoria_id) : rotulo(SUBTIPO, l.subtipo), l.valor,
      pessoa(l.colaborador_id), placa(l.veiculo_id), viagem(l.viagem_id), carga(l.frete_id), rotulo(PAGO_POR, l.pago_por),
      rotulo(FORMA, l.forma), rotulo(CONDICAO, l.condicao), l.fornecedor, l.documento_numero, l.sem_nota_motivo, l.litros, l.hodometro, l.descricao,
      (l.manutencao_itens ?? []).map((id) => itensDeManutencao.get(id)?.nome ?? '?').join(', ') || null,
      contas.get(l.recorrencia_id)?.descricao ?? null, l.competencia, acerto(l.acerto_id), diaDoMomento(l.assinado_em), rotulo(ORIGEM, l.origem),
    ]));

  aba('Parcelas',
    [['Compra', 'texto', 36], ['Parcela', 'texto', 9], ['Vencimento', 'data', 12], ['Valor', 'dinheiro', 12], ['Paga em', 'data', 12], ['Forma', 'texto', 13]],
    t('parcelas').sort(crescente('vencimento', 'numero')).map((p) => [
      despesa(p.lancamento_id), `${p.numero} de ${p.total}`, p.vencimento, p.valor, p.pago_em, rotulo(FORMA, p.forma),
    ]));

  aba('Acertos',
    [['Número', 'texto', 10], ['Pessoa', 'texto', 26], ['Data', 'data', 12], ['Desde', 'data', 12], ['Situação', 'texto', 10],
      ['Saldo anterior', 'dinheiro', 12], ['Vales', 'dinheiro', 12], ['Despesas pagas pela pessoa', 'dinheiro', 14], ['Créditos', 'dinheiro', 12],
      ['Descontos', 'dinheiro', 12], ['Saldo', 'dinheiro', 12], ['O saldo quer dizer', 'texto', 20], ['Quitação', 'texto', 24],
      ['Quitado em', 'data', 12], ['Assinado em', 'data', 12], ['Observação', 'texto', 30]],
    t('acertos').sort(crescente('ano', 'numero', 'data')).map((a) => [
      a.numero ? `${a.numero}/${a.ano}` : 'rascunho', pessoa(a.colaborador_id), a.data, a.periodo_de, rotulo(STATUS_ACERTO, a.status),
      a.saldo_anterior, a.total_vales, a.total_despesas, a.total_creditos, a.total_descontos, a.saldo,
      Number(a.saldo) > 0 ? 'a pessoa devolve' : Number(a.saldo) < 0 ? 'a empresa paga' : 'zerado',
      rotulo(QUITACAO, a.quitacao), a.quitado_em, diaDoMomento(a.assinado_em), a.observacao,
    ]));

  aba('Fotos e documentos',
    [['Data', 'data', 12], ['De', 'texto', 9], ['Qual', 'texto', 36], ['Tipo', 'texto', 8], ['Arquivo', 'texto', 50], ['Tamanho (KB)', 'numero', 11]],
    t('anexos').sort(crescente('created_at')).map((x) => [
      diaDoMomento(x.created_at), x.lancamento_id ? 'Despesa' : x.frete_id ? 'Carga' : x.acerto_id ? 'Acerto' : null,
      x.lancamento_id ? despesa(x.lancamento_id) : x.frete_id ? carga(x.frete_id) : acerto(x.acerto_id),
      rotulo(TIPO_ANEXO, x.tipo), x.storage_path?.split('/').pop() ?? null, x.bytes ? Math.round(x.bytes / 102.4) / 10 : null,
    ]));

  aba('Manutenção preventiva',
    [['Caminhão', 'texto', 11], ['Item', 'texto', 28], ['A cada (km)', 'inteiro', 11], ['A cada (meses)', 'inteiro', 10],
      ['Última vez informada (data)', 'data', 13], ['Última vez informada (km)', 'inteiro', 12], ['Última pelas despesas (data)', 'data', 13],
      ['Última pelas despesas (km)', 'inteiro', 12], ['Observação', 'texto', 30]],
    t('manutencao_itens').sort((a, b) => texto(placa(a.veiculo_id), placa(b.veiculo_id)) || texto(a.nome, b.nome)).map((i) => [
      placa(i.veiculo_id), i.nome, i.intervalo_km, i.intervalo_meses, i.base_data, i.base_km, i.feita_data, i.feita_km, i.observacao,
    ]));

  aba('Oficinas de confiança',
    [['Nome', 'texto', 28], ['Telefone', 'texto', 16], ['WhatsApp', 'sim/não', 10], ['Endereço', 'texto', 30], ['Cidade', 'texto', 20],
      ['Atende', 'texto', 30], ['Latitude', 'numero', 11], ['Longitude', 'numero', 11], ['Ativa', 'sim/não', 8], ['Observação', 'texto', 30]],
    t('oficinas').sort((a, b) => String(a.nome).localeCompare(String(b.nome), 'pt-BR')).map((o) => [
      o.nome, o.telefone, Boolean(o.whatsapp), o.endereco, lugar(o.cidade, o.uf),
      (o.tipos ?? []).length ? o.tipos.map((x) => TIPOS_DE_PROBLEMA.find((tp) => tp.id === x)?.nome ?? x).join(', ') : 'tudo',
      o.latitude, o.longitude, o.ativo !== false, o.observacao,
    ]));

  return abas;
}

// Os arquivos da cópia: [{ nome, dados (Uint8Array) }].
export function arquivosDaCopia({ empresa, tabelas, geradaEm, versao }) {
  const abas = abasDaCopia(empresa, tabelas);
  const nomeDaEmpresa = empresa.nome_fantasia || empresa.nome;
  const contagem = abas.filter((a) => a.nome !== 'Empresa').map((a) => `  - ${a.nome}: ${a.linhas.length} ${a.linhas.length === 1 ? 'linha' : 'linhas'}`);
  const leiaMe = [
    `Cópia dos dados de ${nomeDaEmpresa} no HSA Frota, feita em ${geradaEm}.`,
    '',
    `- ${NOME_DA_PLANILHA}: planilha do Excel, com uma aba para cada assunto. Não traz o que está na lixeira do app.`,
    ...contagem,
    '- dados.json: tudo, inclusive a lixeira e os códigos internos de cada registro, para levar a outro sistema.',
    '',
    'As fotos dos comprovantes vão num .zip à parte: Conta e empresa → "Baixar as fotos dos comprovantes".',
    '',
  ].join('\r\n');
  const json = JSON.stringify({ geradoPor: `HSA Frota ${versao ?? ''}`.trim(), geradoEm: geradaEm, empresa, tabelas }, null, 2);
  return [
    { nome: 'LEIA-ME.txt', dados: strToU8(`\ufeff${leiaMe}`) },
    { nome: NOME_DA_PLANILHA, dados: planilhaXlsx(abas) },
    { nome: 'dados.json', dados: strToU8(json) },
  ];
}

// "hsa-frota-dados-transportes-hebert-2026-10-06.zip"
export function nomeDaCopia(empresa, dia) {
  const base = String(empresa.nome_fantasia || empresa.nome || 'empresa')
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 40) || 'empresa';
  return `hsa-frota-dados-${base}-${dia}.zip`;
}

// Fotos e documentos de um ano, com o nome do arquivo dentro do .zip
// ("2026-09/2026-09-05 despesa 1a2b3c4d.jpg"), sem repetir.
const EXTENSOES = { 'image/jpeg': 'jpg', 'image/png': 'png', 'image/webp': 'webp', 'image/heic': 'heic', 'application/pdf': 'pdf' };

export function anosDasFotos(anexos) {
  return [...new Set(anexos.filter((a) => !a.deleted_at && a.storage_path).map((a) => anoDoAnexo(a)).filter(Boolean))].sort().reverse();
}

function anoDoAnexo(a) {
  // O caminho é {empresa}/{ano}/{mes}/{arquivo}
  return /^[^/]+\/(\d{4})\//.exec(a.storage_path ?? '')?.[1] ?? ((a.created_at ?? '').slice(0, 4) || null);
}

export function fotosDoAno(anexos, ano) {
  const usados = new Set();
  return anexos
    .filter((a) => !a.deleted_at && a.storage_path && anoDoAnexo(a) === ano)
    .sort((a, b) => (a.storage_path < b.storage_path ? -1 : 1))
    .map((a) => {
      const [, , mes] = a.storage_path.split('/');
      const dia = (a.created_at ?? '').slice(0, 10) || `${ano}-${mes ?? '00'}`;
      const ext = EXTENSOES[a.mime] ?? (/\.([a-z0-9]{2,5})$/i.exec(a.storage_path)?.[1]?.toLowerCase() || 'bin');
      const tipo = a.lancamento_id ? 'despesa' : a.frete_id ? 'carga' : 'documento';
      const base = `${ano}-${mes ?? '00'}/${dia} ${tipo} ${String(a.id).slice(0, 8)}`;
      let nome = `${base}.${ext}`;
      for (let i = 2; usados.has(nome); i++) nome = `${base} (${i}).${ext}`;
      usados.add(nome);
      return { anexo: a, nome };
    });
}
